import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { 
  Building2, Users, FileText, Calendar, ArrowLeft, Search, Trash2, 
  RefreshCw, AlertTriangle, CheckCircle2, User, PawPrint 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SuperAdminRAZ = () => {
  const navigate = useNavigate();
  
  // Club selection
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [razSummary, setRazSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Tab data
  const [users, setUsers] = useState([]);
  const [factures, setFactures] = useState([]);
  const [horses, setHorses] = useState([]);
  const [cours, setCours] = useState([]);
  
  // Selections
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedFactures, setSelectedFactures] = useState(new Set());
  const [selectedHorses, setSelectedHorses] = useState(new Set());
  const [selectedCours, setSelectedCours] = useState(new Set());
  
  // Search filters
  const [userSearch, setUserSearch] = useState('');
  const [factureSearch, setFactureSearch] = useState('');
  const [factureFromDate, setFactureFromDate] = useState('');
  const [factureToDate, setFactureToDate] = useState('');
  const [horseSearch, setHorseSearch] = useState('');
  const [coursSearch, setCoursSearch] = useState('');
  const [coursFromDate, setCoursFromDate] = useState('');
  const [coursToDate, setCoursToDate] = useState('');
  
  // Loading states
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingFactures, setLoadingFactures] = useState(false);
  const [loadingHorses, setLoadingHorses] = useState(false);
  const [loadingCours, setLoadingCours] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Fetch clubs list
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/superadmin/clubs`);
        setClubs(response.data);
      } catch (error) {
        toast.error("Impossible de charger la liste des clubs");
      } finally {
        setLoading(false);
      }
    };
    fetchClubs();
  }, []);

  // Fetch summary when club is selected
  useEffect(() => {
    if (selectedClubId) {
      fetchRazSummary();
      // Clear previous data
      setUsers([]);
      setFactures([]);
      setHorses([]);
      setCours([]);
      setSelectedUsers(new Set());
      setSelectedFactures(new Set());
      setSelectedHorses(new Set());
      setSelectedCours(new Set());
    }
  }, [selectedClubId]);

  const fetchRazSummary = async () => {
    if (!selectedClubId) return;
    try {
      const response = await axios.get(`${API_URL}/api/superadmin/clubs/${selectedClubId}/raz-summary`);
      setRazSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // ============ FETCH FUNCTIONS ============
  const fetchUsers = useCallback(async () => {
    if (!selectedClubId) return;
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      if (userSearch) params.append('search', userSearch);
      const response = await axios.get(`${API_URL}/api/superadmin/clubs/${selectedClubId}/users?${params}`);
      setUsers(response.data.users);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des utilisateurs"));
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedClubId, userSearch]);

  const fetchFactures = useCallback(async () => {
    if (!selectedClubId) return;
    setLoadingFactures(true);
    try {
      const params = new URLSearchParams();
      if (factureSearch) params.append('search', factureSearch);
      if (factureFromDate) params.append('date_from', factureFromDate);
      if (factureToDate) params.append('date_to', factureToDate);
      const response = await axios.get(`${API_URL}/api/superadmin/clubs/${selectedClubId}/factures?${params}`);
      setFactures(response.data.factures);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des factures"));
    } finally {
      setLoadingFactures(false);
    }
  }, [selectedClubId, factureSearch, factureFromDate, factureToDate]);

  const fetchHorses = useCallback(async () => {
    if (!selectedClubId) return;
    setLoadingHorses(true);
    try {
      const params = new URLSearchParams();
      if (horseSearch) params.append('search', horseSearch);
      const response = await axios.get(`${API_URL}/api/superadmin/clubs/${selectedClubId}/horses?${params}`);
      setHorses(response.data.horses);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des chevaux"));
    } finally {
      setLoadingHorses(false);
    }
  }, [selectedClubId, horseSearch]);

  const fetchCours = useCallback(async () => {
    if (!selectedClubId) return;
    setLoadingCours(true);
    try {
      const params = new URLSearchParams();
      if (coursSearch) params.append('search', coursSearch);
      if (coursFromDate) params.append('date_from', coursFromDate);
      if (coursToDate) params.append('date_to', coursToDate);
      const response = await axios.get(`${API_URL}/api/superadmin/clubs/${selectedClubId}/cours?${params}`);
      setCours(response.data.cours);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des cours"));
    } finally {
      setLoadingCours(false);
    }
  }, [selectedClubId, coursSearch, coursFromDate, coursToDate]);

  // ============ SELECTION HANDLERS ============
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const selectAllUsers = () => {
    // Don't select instructeur_admin users
    const selectableUsers = users.filter(u => u.role_club !== 'instructeur_admin');
    setSelectedUsers(new Set(selectableUsers.map(u => u.id)));
  };

  const deselectAllUsers = () => setSelectedUsers(new Set());

  const toggleFactureSelection = (factureId) => {
    setSelectedFactures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(factureId)) {
        newSet.delete(factureId);
      } else {
        newSet.add(factureId);
      }
      return newSet;
    });
  };

  const selectAllFactures = () => setSelectedFactures(new Set(factures.map(f => f.id)));
  const deselectAllFactures = () => setSelectedFactures(new Set());

  const toggleHorseSelection = (horseId) => {
    setSelectedHorses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(horseId)) {
        newSet.delete(horseId);
      } else {
        newSet.add(horseId);
      }
      return newSet;
    });
  };

  const selectAllHorses = () => setSelectedHorses(new Set(horses.map(h => h.id)));
  const deselectAllHorses = () => setSelectedHorses(new Set());

  const toggleCoursSelection = (coursId) => {
    setSelectedCours(prev => {
      const newSet = new Set(prev);
      if (newSet.has(coursId)) {
        newSet.delete(coursId);
      } else {
        newSet.add(coursId);
      }
      return newSet;
    });
  };

  const selectAllCours = () => setSelectedCours(new Set(cours.map(c => c.id)));
  const deselectAllCours = () => setSelectedCours(new Set());

  // ============ DELETE HANDLERS ============
  const openDeleteDialog = (type) => {
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      let endpoint = '';
      let ids = [];
      
      switch (deleteType) {
        case 'users':
          endpoint = `/api/superadmin/clubs/${selectedClubId}/users/bulk`;
          ids = Array.from(selectedUsers);
          break;
        case 'factures':
          endpoint = `/api/superadmin/clubs/${selectedClubId}/factures/bulk`;
          ids = Array.from(selectedFactures);
          break;
        case 'horses':
          endpoint = `/api/superadmin/clubs/${selectedClubId}/horses/bulk`;
          ids = Array.from(selectedHorses);
          break;
        case 'cours':
          endpoint = `/api/superadmin/clubs/${selectedClubId}/cours/bulk`;
          ids = Array.from(selectedCours);
          break;
        default:
          return;
      }
      
      const bodyKey = deleteType === 'users' ? 'user_ids' : 
                      deleteType === 'factures' ? 'facture_ids' :
                      deleteType === 'horses' ? 'horse_ids' : 'cours_ids';
      
      const response = await axios.delete(`${API_URL}${endpoint}`, {
        data: { [bodyKey]: ids }
      });
      
      toast.success(response.data.message);
      
      // Refresh data
      switch (deleteType) {
        case 'users':
          fetchUsers();
          setSelectedUsers(new Set());
          break;
        case 'factures':
          fetchFactures();
          setSelectedFactures(new Set());
          break;
        case 'horses':
          fetchHorses();
          setSelectedHorses(new Set());
          break;
        case 'cours':
          fetchCours();
          setSelectedCours(new Set());
          break;
      }
      
      fetchRazSummary();
      
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la suppression"));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const getDeleteSummary = () => {
    switch (deleteType) {
      case 'users':
        return `${selectedUsers.size} utilisateur(s)`;
      case 'factures':
        return `${selectedFactures.size} facture(s)`;
      case 'horses':
        return `${selectedHorses.size} cheval(aux)`;
      case 'cours':
        return `${selectedCours.size} cours/événement(s)`;
      default:
        return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', { 
      dateStyle: 'short', 
      timeStyle: 'short' 
    });
  };

  const getRoleBadge = (role) => {
    if (role === 'instructeur_admin') {
      return <Badge className="bg-purple-100 text-purple-800">Admin</Badge>;
    } else if (role === 'instructeur') {
      return <Badge className="bg-blue-100 text-blue-800">Instructeur</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800">Cavalier</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <Building2 className="w-6 h-6 text-[#2D5A3D]" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Administration Club - RAZ</h1>
                <p className="text-sm text-gray-500">Réinitialisation des données de test</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Club Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Sélection du club</CardTitle>
            <CardDescription>
              Sélectionnez le club dont vous souhaitez réinitialiser les données de test
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-md">
                <Select value={selectedClubId} onValueChange={setSelectedClubId}>
                  <SelectTrigger data-testid="club-selector">
                    <SelectValue placeholder="Choisir un club..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clubs.map(club => (
                      <SelectItem key={club.id} value={club.id}>
                        {club.nom} {!club.installed && '(non installé)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {razSummary && (
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span>{razSummary.users_count} utilisateurs ({razSummary.admin_count} admin)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span>{razSummary.factures_count} factures</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PawPrint className="w-4 h-4 text-gray-500" />
                    <span>{razSummary.horses_count} chevaux</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span>{razSummary.cours_count} cours</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Warning */}
        {selectedClubId && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Attention : Suppressions définitives</p>
              <p className="text-sm text-amber-700 mt-1">
                Les suppressions effectuées ici sont irréversibles. Assurez-vous de sélectionner uniquement les données de test à supprimer.
                Au moins un instructeur administrateur doit rester dans le club.
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        {selectedClubId && (
          <Tabs defaultValue="users" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Utilisateurs
              </TabsTrigger>
              <TabsTrigger value="factures" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Factures
              </TabsTrigger>
              <TabsTrigger value="horses" className="flex items-center gap-2">
                <PawPrint className="w-4 h-4" />
                Chevaux
              </TabsTrigger>
              <TabsTrigger value="cours" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Cours/Événements
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Gestion des utilisateurs</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllUsers}>
                        Tout sélectionner
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllUsers}>
                        Tout désélectionner
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => openDeleteDialog('users')}
                        disabled={selectedUsers.size === 0}
                        data-testid="delete-users-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer ({selectedUsers.size})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher par nom, prénom, email, date de naissance..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-10"
                        data-testid="user-search-input"
                      />
                    </div>
                    <Button onClick={fetchUsers} disabled={loadingUsers}>
                      <Search className="w-4 h-4 mr-2" />
                      Rechercher
                    </Button>
                  </div>

                  {/* Users table */}
                  {loadingUsers ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : users.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      Cliquez sur "Rechercher" pour charger les utilisateurs
                    </p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left w-10"></th>
                            <th className="px-4 py-3 text-left">Nom Prénom (Pseudo)</th>
                            <th className="px-4 py-3 text-left">Email</th>
                            <th className="px-4 py-3 text-left">Date naissance</th>
                            <th className="px-4 py-3 text-left">Rôle</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {users.map(user => (
                            <tr 
                              key={user.id} 
                              className={`hover:bg-gray-50 ${user.role_club === 'instructeur_admin' ? 'bg-purple-50' : ''}`}
                            >
                              <td className="px-4 py-3">
                                {user.role_club !== 'instructeur_admin' ? (
                                  <Checkbox
                                    checked={selectedUsers.has(user.id)}
                                    onCheckedChange={() => toggleUserSelection(user.id)}
                                    data-testid={`user-checkbox-${user.id}`}
                                  />
                                ) : (
                                  <span className="text-xs text-purple-600" title="Les admins ne peuvent pas être sélectionnés">🔒</span>
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium">
                                {user.nom} {user.prenom}
                                {user.pseudo && <span className="text-gray-500 font-normal"> ({user.pseudo})</span>}
                              </td>
                              <td className="px-4 py-3 text-gray-600">{user.email}</td>
                              <td className="px-4 py-3">{formatDate(user.date_naissance)}</td>
                              <td className="px-4 py-3">{getRoleBadge(user.role_club)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Factures Tab */}
            <TabsContent value="factures">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Gestion des factures</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllFactures}>
                        Tout sélectionner
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllFactures}>
                        Tout désélectionner
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => openDeleteDialog('factures')}
                        disabled={selectedFactures.size === 0}
                        data-testid="delete-factures-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer ({selectedFactures.size})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search */}
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex-1 min-w-[200px] relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher par nom/prénom cavalier..."
                        value={factureSearch}
                        onChange={(e) => setFactureSearch(e.target.value)}
                        className="pl-10"
                        data-testid="facture-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Du:</Label>
                      <Input
                        type="date"
                        value={factureFromDate}
                        onChange={(e) => setFactureFromDate(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Au:</Label>
                      <Input
                        type="date"
                        value={factureToDate}
                        onChange={(e) => setFactureToDate(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <Button onClick={fetchFactures} disabled={loadingFactures}>
                      <Search className="w-4 h-4 mr-2" />
                      Rechercher
                    </Button>
                  </div>

                  {/* Factures table */}
                  {loadingFactures ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : factures.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      Cliquez sur "Rechercher" pour charger les factures
                    </p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left w-10"></th>
                            <th className="px-4 py-3 text-left">N° Facture</th>
                            <th className="px-4 py-3 text-left">Date</th>
                            <th className="px-4 py-3 text-left">Cavalier</th>
                            <th className="px-4 py-3 text-right">Montant</th>
                            <th className="px-4 py-3 text-left">Statut</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {factures.map(facture => (
                            <tr key={facture.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={selectedFactures.has(facture.id)}
                                  onCheckedChange={() => toggleFactureSelection(facture.id)}
                                  data-testid={`facture-checkbox-${facture.id}`}
                                />
                              </td>
                              <td className="px-4 py-3 font-mono text-xs">
                                {facture.type_document === 'avoir' && (
                                  <Badge className="bg-red-100 text-red-700 mr-1 text-xs">AVOIR</Badge>
                                )}
                                {facture.numero_facture || <span className="text-gray-400 italic">Brouillon</span>}
                              </td>
                              <td className="px-4 py-3">{formatDate(facture.date_emission)}</td>
                              <td className="px-4 py-3">
                                {facture.cavalier_prenom} {facture.cavalier_nom}
                              </td>
                              <td className="px-4 py-3 text-right font-medium">
                                <span className={facture.total_ttc < 0 ? 'text-red-600' : ''}>
                                  {facture.total_ttc?.toFixed(2) || '0.00'} €
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant="outline">{facture.statut}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Horses Tab */}
            <TabsContent value="horses">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Gestion des chevaux</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllHorses}>
                        Tout sélectionner
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllHorses}>
                        Tout désélectionner
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => openDeleteDialog('horses')}
                        disabled={selectedHorses.size === 0}
                        data-testid="delete-horses-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer ({selectedHorses.size})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher par nom du cheval ou propriétaire..."
                        value={horseSearch}
                        onChange={(e) => setHorseSearch(e.target.value)}
                        className="pl-10"
                        data-testid="horse-search-input"
                      />
                    </div>
                    <Button onClick={fetchHorses} disabled={loadingHorses}>
                      <Search className="w-4 h-4 mr-2" />
                      Rechercher
                    </Button>
                  </div>

                  {/* Horses table */}
                  {loadingHorses ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : horses.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      Cliquez sur "Rechercher" pour charger les chevaux
                    </p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left w-10"></th>
                            <th className="px-4 py-3 text-left">Nom</th>
                            <th className="px-4 py-3 text-left">Race</th>
                            <th className="px-4 py-3 text-left">Propriétaire</th>
                            <th className="px-4 py-3 text-left">Statut</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {horses.map(horse => (
                            <tr key={horse.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={selectedHorses.has(horse.id)}
                                  onCheckedChange={() => toggleHorseSelection(horse.id)}
                                  data-testid={`horse-checkbox-${horse.id}`}
                                />
                              </td>
                              <td className="px-4 py-3 font-medium">{horse.nom}</td>
                              <td className="px-4 py-3">{horse.race || '-'}</td>
                              <td className="px-4 py-3">{horse.proprietaire_nom || 'Club'}</td>
                              <td className="px-4 py-3">
                                <Badge variant={horse.statut === 'actif' ? 'default' : 'secondary'}>
                                  {horse.statut}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Cours Tab */}
            <TabsContent value="cours">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Gestion des cours/événements</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllCours}>
                        Tout sélectionner
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllCours}>
                        Tout désélectionner
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => openDeleteDialog('cours')}
                        disabled={selectedCours.size === 0}
                        data-testid="delete-cours-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer ({selectedCours.size})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search */}
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex-1 min-w-[200px] relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher par type d'activité..."
                        value={coursSearch}
                        onChange={(e) => setCoursSearch(e.target.value)}
                        className="pl-10"
                        data-testid="cours-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Du:</Label>
                      <Input
                        type="date"
                        value={coursFromDate}
                        onChange={(e) => setCoursFromDate(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Au:</Label>
                      <Input
                        type="date"
                        value={coursToDate}
                        onChange={(e) => setCoursToDate(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <Button onClick={fetchCours} disabled={loadingCours}>
                      <Search className="w-4 h-4 mr-2" />
                      Rechercher
                    </Button>
                  </div>

                  {/* Cours table */}
                  {loadingCours ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : cours.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      Cliquez sur "Rechercher" pour charger les cours
                    </p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left w-10"></th>
                            <th className="px-4 py-3 text-left">Date/Heure</th>
                            <th className="px-4 py-3 text-left">Type</th>
                            <th className="px-4 py-3 text-left">Activité</th>
                            <th className="px-4 py-3 text-left">Instructeur</th>
                            <th className="px-4 py-3 text-left">Récurrence</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {cours.map(c => (
                            <tr key={c.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={selectedCours.has(c.id)}
                                  onCheckedChange={() => toggleCoursSelection(c.id)}
                                  data-testid={`cours-checkbox-${c.id}`}
                                />
                              </td>
                              <td className="px-4 py-3 text-xs">{formatDateTime(c.date_heure_debut)}</td>
                              <td className="px-4 py-3">
                                <Badge variant="outline">{c.type_cours}</Badge>
                              </td>
                              <td className="px-4 py-3 font-medium">{c.type_activite || '-'}</td>
                              <td className="px-4 py-3">
                                {c.instructeur_prenom} {c.instructeur_nom}
                              </td>
                              <td className="px-4 py-3 text-gray-500">{c.recurrence || 'unique'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* No club selected */}
        {!selectedClubId && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Sélectionnez un club
            </h3>
            <p className="text-gray-500">
              Choisissez un club dans la liste ci-dessus pour accéder aux outils de réinitialisation
            </p>
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer définitivement <strong>{getDeleteSummary()}</strong>.
              <br /><br />
              Cette action est irréversible. Êtes-vous sûr de vouloir continuer ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Suppression...' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuperAdminRAZ;
